import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pagination } from "antd";

import { SchoolAction } from "../../store/actions";

import {
  boxContainer,
  Spacer,
  inputFields,
  Buttons,
  popover,
  tables,
} from "../../components";

import { UtilityArrays } from "../../config";

const Schools = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { schools, count, isSchoolsLoading } = useSelector(
    (state) => state.schools
  );

  const [pageNo, setPageNo] = useState(1);

  const [filters, setFilters] = useState({
    search: "",
    limit: 10,
    offset: 0,
    status: "",
  });

  useEffect(() => {
    handleSearch();
  }, [filters]);

  const onShowSizeChange = (current, pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      limit: pageSize,
      offset: (current - 1) * pageSize,
    }));
    setPageNo(current);
  };

  const handlePageChange = (pageNumber) => {
    setPageNo(pageNumber);
    setFilters((prevFilters) => ({
      ...prevFilters,
      offset: (pageNumber - 1) * prevFilters.limit,
    }));
  };

  const handleFilterChange = (updatedFilter) => {
    setPageNo(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilter,
      offset: 0,
    }));
  };

  const handleSearch = async () => {
    const payload = { ...filters };
    dispatch(SchoolAction.getSchools(payload));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleReset = async () => {
    setFilters({
      ...filters,
      search: "",
      limit: 10,
      offset: 0,
      status: "",
    });
    setPageNo(1);
  };

  const handleUpdate = (userId, status) => {
    const payload = { userId, status };
    dispatch(SchoolAction.changeSchoolStatus(payload, () => handleSearch()));
  };

  const statusArray = [
    { title: "All", value: "" },
    { title: "Active", value: "active" },
    { title: "Deleted", value: "deleted" },
    { title: "Deactivated", value: "deactivated" },
    { title: "Pending", value: "pending" },
  ];
  const updateStatusArray = [
    { label: "Active", value: "active" },
    { label: "Deleted", value: "deleted" },
    { label: "Deactivated", value: "deactivated" },
    { label: "Pending", value: "pending" },
  ];

  const filterButtons = [
    {
      title: "Status",
      items: (
        <div className="filter-dropdown">
          {statusArray?.map((item, index) => (
            <div
              key={index}
              onClick={() => {
                handleFilterChange({ status: item.value });
              }}
              className={`${
                filters.status === item.value
                  ? "active-filter-value-list"
                  : "filter-value-list"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
  ];

  const columns = [
    {
      title: " ",
      dataIndex: "index",
      key: "0",
      width: 5,
      render: (text, record, index) => (
        <div className="flex gap-2 items-center">{index + 1}</div>
      ),
    },
    {
      title: "School Name",
      dataIndex: "name",
      key: "1",
      width: 250,
      render: (text, record) => (
        <div className="flex gap-2 items-center">
          <img
            src={record?.profileImage}
            className="h-[3.75em] w-[3.75em] border border-searchBorder shadow-boxContainer rounded-lg object-contain bg-white"
          />

          <div className="font-semibold">
            {record?.firstName} {record?.lastName}
          </div>
        </div>
      ),
    },

    {
      title: "Email",
      dataIndex: "email",
      key: "2",
      width: 150,
      align: "center",
      render: (text, record) => <div>{record?.email ?? "NA"}</div>,
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "4",
      width: 150,
      align: "center",
      render: (text, record) => (
        <div>{record?.phoneNumber ? `+${record?.phoneNumber}` : "NA"}</div>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "5",
      width: 150,
      align: "center",
      render: (text, record) => (
        <div>
          {record?.city}, {record?.country}
        </div>
      ),
    },
    {
      title: "Total Students",
      dataIndex: "totalStudents",
      key: "6",
      width: 120,
      align: "center",
      render: (text, record) => <div>{record?.enlistedStudents}</div>,
    },
    {
      title: "Registered Students",
      dataIndex: "registeredStudents",
      key: "7",
      width: 120,
      align: "center",
      render: (text, record) => <div>{record?.registeredStudents}</div>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "10",
      width: 150,
      align: "center",
      render: (text, record) => (
        <div className="w-[10.625em]" onClick={(e) => e.stopPropagation()}>
          <inputFields.SelectDropdown
            items={updateStatusArray}
            value={record?.status ?? null}
            onChange={(e) => handleUpdate(record?.id, e)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="text-black">
      <div className="flex gap-3 justify-center">
        <inputFields.DefaultSearchBox
          onChange={(e) => {
            handleFilterChange({ search: e.target.value });
          }}
          value={filters.search}
          onKeyDown={handleKeyDown}
        />
        <div className="w-[9.375em]">
          <Buttons.DefaultButton
            width="full"
            style={{ height: "100%" }}
            size="medium"
            onClick={() => handleSearch()}
          >
            Search
          </Buttons.DefaultButton>
        </div>
      </div>
      <Spacer height={10} />
      <div className="flex justify-between">
        <div className="flex gap-2 items-center">
          <div className="text-[0.875em] opacity-80">Filter:</div>
          {filterButtons?.map((item, index) => (
            <popover.WhitePopover
              content={item.items}
              placement={item?.placement ?? "bottom"}
              key={index}
            >
              <Buttons.DefaultButton size="small">
                {item?.title}
              </Buttons.DefaultButton>
            </popover.WhitePopover>
          ))}
        </div>
        {Object.values(filters).some(
          (value) => value !== "" && value?.length > 0
        ) && (
          <div className="font-semibold cursor-pointer" onClick={handleReset}>
            Clear Filters
          </div>
        )}
      </div>
      <Spacer height={10} />
      <boxContainer.LiteBoxContainer addstyle={"!p-0"}>
        <div>
          <tables.DefaultTable
            columns={columns}
            data={schools}
            borderradius={0}
            rowKey={() => Math.random()}
            loading={isSchoolsLoading}
            onRow={(record, index) => ({
              onClick: (event) => {
                const clickedElement = event.target.closest("td");
                const columnIndex = Array.from(
                  clickedElement?.parentNode?.children || []
                ).indexOf(clickedElement);

                if (![7].includes(columnIndex)) {
                  if (event.ctrlKey) {
                    window.open(`/schools/${record.id}`, "_blank");
                  } else {
                    navigate(`/schools/${record.id}`);
                  }
                }
              },
            })}
          />
        </div>
        <div className="flex justify-center items-center p-3">
          <Pagination
            responsive
            hideOnSinglePage={false}
            current={pageNo}
            pageSize={filters.limit}
            total={count}
            showSizeChanger
            pageSizeOptions={UtilityArrays?.pageSizes}
            onShowSizeChange={onShowSizeChange}
            onChange={handlePageChange}
          />
        </div>
      </boxContainer.LiteBoxContainer>
    </div>
  );
};

export default Schools;
